/**
 * Leviathan Web - Customer Location Form (Create/Edit)
 * Quasar Prime LLC
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { parseApiError } from '@/utils/errorHandler';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';

import { API_URL } from '@/config/api';

const LOCATION_TYPES = [
  { value: 'PROJECT_SITE', label: 'Project Site' },
  { value: 'WAREHOUSE', label: 'Warehouse' },
  { value: 'FLEET_YARD', label: 'Fleet Yard' },
  { value: 'OFFICE', label: 'Office' },
  { value: 'OTHER', label: 'Other' },
];

interface FormData {
  name: string;
  locationCode: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  contactFirstName: string;
  contactLastName: string;
  contactPhone: string;
  contactEmail: string;
  locationType: string;
  instructions: string;
  isActive: boolean;
}

export default function CustomerLocationForm() {
  const { customerId, id } = useParams();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState<FormData>({
    name: '',
    locationCode: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    contactFirstName: '',
    contactLastName: '',
    contactPhone: '',
    contactEmail: '',
    locationType: '',
    instructions: '',
    isActive: true,
  });

  const [customerName, setCustomerName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchCustomer();
    if (isEditing) {
      fetchLocation();
    }
  }, [customerId, id]);

  const fetchCustomer = async () => {
    try {
      const response = await fetch(`${API_URL}/api/customers/${customerId}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setCustomerName(data.customer.name);
      }
    } catch (err) {
      console.error('Failed to fetch customer:', err);
    }
  };

  const fetchLocation = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/customer-locations/${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new Error(errorMessage);
      }

      const data = await response.json();
      const loc = data.location;
      setFormData({
        name: loc.name || '',
        locationCode: loc.locationCode || '',
        address: loc.address || '',
        city: loc.city || '',
        state: loc.state || '',
        zip: loc.zip || '',
        contactFirstName: loc.contactFirstName || '',
        contactLastName: loc.contactLastName || '',
        contactPhone: loc.contactPhone || '',
        contactEmail: loc.contactEmail || '',
        locationType: loc.locationType || '',
        instructions: loc.instructions || '',
        isActive: loc.isActive,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch location');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.name.trim()) {
      setError('Location name is required');
      return;
    }

    setIsSaving(true);
    try {
      const url = isEditing
        ? `${API_URL}/api/customer-locations/${id}`
        : `${API_URL}/api/customer-locations`;

      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          customerId,
          ...formData,
        }),
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new Error(errorMessage);
      }

      const data = await response.json();
      navigate(`/customers/${customerId}/locations/${data.location.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save location');
    } finally {
      setIsSaving(false);
    }
  };

  const updateField = (field: keyof FormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="animate-spin text-amber" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="relative">
          <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber" />
          <div className="pl-6">
            <Link
              to={`/customers/${customerId}/locations`}
              className="back-link mb-3"
            >
              <ArrowLeft size={14} />
              Back to Locations
            </Link>
            <h1
              className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
              style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
            >
              {isEditing ? 'Edit Location' : 'New Location'}
            </h1>
            <p className="text-text-secondary text-sm font-mono mt-1">{customerName}</p>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-error-red/10 border border-error-red/50 text-error-red p-4 font-mono text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Location Information */}
        <Panel title="Location Information">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Location Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => updateField('name', e.target.value)}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono"
                placeholder="e.g., Highway 290 Project Site"
                required
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Location Code
              </label>
              <input
                type="text"
                value={formData.locationCode}
                onChange={(e) => updateField('locationCode', e.target.value.toUpperCase())}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono"
                placeholder="e.g., SITE-A"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Location Type
              </label>
              <select
                value={formData.locationType}
                onChange={(e) => updateField('locationType', e.target.value)}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono"
              >
                <option value="">Select Type</option>
                {LOCATION_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </Panel>

        {/* Address */}
        <Panel title="Address">
          <div className="grid grid-cols-1 gap-6">
            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Street Address
              </label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => updateField('address', e.target.value)}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono"
                placeholder="123 Industrial Blvd"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                  City
                </label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => updateField('city', e.target.value)}
                  className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono"
                />
              </div>

              <div>
                <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                  State
                </label>
                <input
                  type="text"
                  value={formData.state}
                  onChange={(e) => updateField('state', e.target.value.toUpperCase().slice(0, 2))}
                  className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono"
                  maxLength={2}
                  placeholder="TX"
                />
              </div>

              <div>
                <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                  ZIP Code
                </label>
                <input
                  type="text"
                  value={formData.zip}
                  onChange={(e) => updateField('zip', e.target.value)}
                  className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono"
                  placeholder="77001"
                />
              </div>
            </div>
          </div>
        </Panel>

        {/* Contact Information */}
        <Panel title="Contact at Location">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Split, because contact lists sort by surname. */}
            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Contact First Name
              </label>
              <input
                type="text"
                value={formData.contactFirstName}
                onChange={(e) => updateField('contactFirstName', e.target.value)}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono"
                placeholder="John"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Contact Last Name
              </label>
              <input
                type="text"
                value={formData.contactLastName}
                onChange={(e) => updateField('contactLastName', e.target.value)}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono"
                placeholder="Smith"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Contact Phone
              </label>
              <input
                type="tel"
                value={formData.contactPhone}
                onChange={(e) => updateField('contactPhone', e.target.value)}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono"
                placeholder="(555) 123-4567"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Contact Email
              </label>
              <input
                type="email"
                value={formData.contactEmail}
                onChange={(e) => updateField('contactEmail', e.target.value)}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono"
                placeholder="john@example.com"
              />
            </div>
          </div>
        </Panel>

        {/* Delivery Instructions */}
        <Panel title="Delivery Instructions">
          <div>
            <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
              Special Instructions
            </label>
            <textarea
              value={formData.instructions}
              onChange={(e) => updateField('instructions', e.target.value)}
              className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono"
              rows={4}
              placeholder="Gate codes, access instructions, special requirements, delivery windows, etc."
            />
          </div>
        </Panel>

        {/* Status (edit only) */}
        {isEditing && (
          <Panel title="Status">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => updateField('isActive', e.target.checked)}
                className="form-checkbox bg-background border-panel-border text-amber focus:ring-amber h-5 w-5"
              />
              <span className="text-text-secondary font-mono text-sm">Location is active</span>
            </label>
            <p className="text-text-tertiary text-xs font-mono mt-2">
              Inactive locations won't appear in order forms or tank assignment lists.
            </p>
          </Panel>
        )}

        {/* Actions */}
        <div className="flex justify-end space-x-4">
          <Link
            to={`/customers/${customerId}/locations`}
            className="bg-panel-dark border border-panel-border hover:border-amber/50 text-text-secondary font-mono py-2 px-6 transition-all uppercase tracking-wider text-sm"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={isSaving}
            className="flex items-center gap-2 bg-amber hover:bg-amber/90 text-background font-mono py-2 px-6 transition-all uppercase tracking-wider text-sm disabled:opacity-50"
          >
            {isSaving ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save size={16} />
                {isEditing ? 'Update Location' : 'Create Location'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="relative">
      {/* Corner decorations */}
      <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
      <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
      <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
      <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

      <div className="bg-panel-dark border border-panel-border p-6">
        <h3 className="text-lg font-display text-text-primary uppercase tracking-wide mb-6 pb-3 border-b border-amber">
          {title}
        </h3>
        {children}
      </div>
    </div>
  );
}
